import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Clock, Send, MessageSquare, Users, Github, Linkedin, Twitter } from 'lucide-react';
import emailjs from '@emailjs/browser';
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';

const Contact = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  // EmailJS configuration - Replace with your actual values
  const SERVICE_ID = 'your_service_id';
  const TEMPLATE_ID = 'your_template_id';
  const PUBLIC_KEY = 'your_public_key';

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(PUBLIC_KEY);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send email using EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_name: 'DMLS Team',
      };

      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );

      // Reset form on success
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setSubmitStatus('success');

    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      description: "General inquiries and support",
      contact: "dmls@dal.ca",
      link: "mailto:dmls@dal.ca"
    },
    {
      icon: Clock,
      title: "Office Hours",
      description: "When we're available",
      contact: "Mon-Fri: 9AM-5PM AST",
      link: "#"
    },
    {
      icon: Users,
      title: "Join Our Community",
      description: "Connect with fellow ML enthusiasts",
      contact: "Apply via our form",
      link: "https://airtable.com/appbzvXorTGCwSsy9/pagrrVOHqpocEY5IX/form"
    }
  ];

  const teamContacts = [
    {
      name: "Anand Lo",
      role: "President",
      email: "president@dmls.ca",
      initials: "AL"
    },
    {
      name: "Het Jivani",
      role: "Internal VP",
      email: "internal.vp@dmls.ca",
      initials: "HJ"
    },
    {
      name: "Kai Patel",
      role: "External VP",
      email: "external.vp@dmls.ca",
      initials: "KP"
    },
    {
      name: "Soban Zaman",
      role: "Secretary",
      email: "secretary@dmls.ca",
      initials: "SZ"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-16 sm:pt-24 pb-8 sm:pb-16 bg-cover bg-center bg-no-repeat relative overflow-hidden" style={{ backgroundImage: 'url(/6.jpg)' }}>
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50 z-0"></div>

        {/* Animated background elements */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 bg-[#0395fc]/20 rounded-full blur-2xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-48 h-48 bg-yellow-300/15 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        <div className="container mx-auto px-4 max-w-sm sm:max-w-2xl lg:max-w-4xl xl:max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold mb-8 backdrop-blur-sm"
            >
              <MessageSquare className="size-4" />
              GET IN TOUCH
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 sm:mb-6 leading-tight"
            >
              Contact
              <br />
              <span className="text-yellow-300">DMLS</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-base sm:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto mb-8 sm:mb-12 px-4"
            >
              Have questions about our events, research opportunities, or want to get involved?
              We'd love to hear from you!
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#0395fc]/10 text-[#0395fc] px-4 py-2 rounded-full border-2 border-[#0395fc] text-sm font-bold mb-6">
              <Mail className="size-4" />
              CONTACT INFORMATION
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4 sm:mb-6">
              Reach Out to Us
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
              Multiple ways to connect with the DMLS community and get the information you need.
            </p>
          </motion.div>

          <div className="flex flex-col sm:flex-row gap-6 lg:gap-8 justify-center max-w-4xl mx-auto">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.a
                  key={index}
                  href={info.link}
                  target={info.link.startsWith('http') ? '_blank' : undefined}
                  rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[12px_12px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 group cursor-pointer block flex-1 max-w-sm"
                >
                  <div className="p-6 sm:p-8 text-center">
                    <div className="bg-[#0395fc] border-2 border-black p-3 sm:p-4 w-fit mx-auto mb-4 sm:mb-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] group-hover:scale-110 transition-transform duration-300">
                      <Icon className="size-6 sm:size-8 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-black text-foreground mb-2 sm:mb-3">{info.title}</h3>
                    <p className="text-sm sm:text-base text-muted-foreground mb-2 sm:mb-3">{info.description}</p>
                    <p className="text-[#0395fc] font-bold text-sm sm:text-base">{info.contact}</p>
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4 sm:mb-6">
                  Send Us a Message
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground">
                  Have a specific question or want to collaborate? Fill out the form below and we'll get back to you.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-foreground mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black bg-white text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#0395fc] focus:border-[#0395fc] transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-black bg-white text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#0395fc] focus:border-[#0395fc] transition-colors"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-bold text-foreground mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-black bg-white text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#0395fc] focus:border-[#0395fc] transition-colors"
                    placeholder="What's this about?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-foreground mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-black bg-white text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#0395fc] focus:border-[#0395fc] transition-colors resize-vertical"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-[#0395fc] hover:bg-[#0395fc]/90 disabled:bg-[#0395fc]/70 text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold border-2 sm:border-4 border-[#0395fc] shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] transition-all duration-200 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="size-5" />
                      Send Message
                    </>
                  )}
                </motion.button>
              </form>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg"
                >
                  <div className="flex items-center gap-2 text-green-800">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="font-bold">Message sent successfully!</p>
                  </div>
                  <p className="text-green-700 mt-1">Thank you for your message. We'll get back to you soon.</p>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg"
                >
                  <div className="flex items-center gap-2 text-red-800">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <p className="font-bold">Failed to send message</p>
                  </div>
                  <p className="text-red-700 mt-1">Please try again later or contact us directly at dmls@dal.ca</p>
                </motion.div>
              )}
            </motion.div>

            {/* Team Contacts */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4 sm:mb-6">
                  Executive Team
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground">
                  Need to reach a specific team member? Here are our executive contacts.
                </p>
              </div>

              <div className="space-y-4">
                {teamContacts.map((member, index) => (
                  <motion.a
                    key={index}
                    href={`mailto:${member.email}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ x: 5 }}
                    className="flex items-center gap-4 p-4 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 group block"
                  >
                    <div className="w-12 h-12 bg-[#0395fc] border-2 border-black rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-black text-sm">{member.initials}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-black text-foreground text-sm sm:text-base">{member.name}</h3>
                      <p className="text-[#0395fc] font-bold text-xs sm:text-sm">{member.role}</p>
                      <p className="text-muted-foreground text-xs sm:text-sm">{member.email}</p>
                    </div>
                    <Mail className="size-4 sm:size-5 text-[#0395fc] group-hover:scale-110 transition-transform duration-200" />
                  </motion.a>
                ))}
              </div>

              {/* Social Links */}
              <div className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-black text-foreground mb-4 sm:mb-6">Follow Us</h3>
                <div className="flex gap-4">
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="bg-[#0395fc] border-2 border-black p-3 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] transition-all duration-200"
                  >
                    <Github className="size-5 text-white" />
                  </motion.a>
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="bg-[#0395fc] border-2 border-black p-3 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] transition-all duration-200"
                  >
                    <Linkedin className="size-5 text-white" />
                  </motion.a>
                  <motion.a
                    href="#"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="bg-[#0395fc] border-2 border-black p-3 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] transition-all duration-200"
                  >
                    <Twitter className="size-5 text-white" />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-full border-2 border-gray-300 text-sm font-bold mb-6">
              <MessageSquare className="size-4" />
              FREQUENTLY ASKED QUESTIONS
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4 sm:mb-6">
              Got Questions?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
              Find quick answers to common questions about DMLS and our activities.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8"
            >
              <h3 className="text-lg sm:text-xl font-black text-foreground mb-3 sm:mb-4">How can I join DMLS?</h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                You can join DMLS by filling out our membership application form. We welcome students from all backgrounds and experience levels who are interested in machine learning and AI.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gray-50 border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8"
            >
              <h3 className="text-lg sm:text-xl font-black text-foreground mb-3 sm:mb-4">Do I need prior experience in ML/AI?</h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                Not at all! DMLS welcomes members at all skill levels. Whether you're a complete beginner or an experienced practitioner, we have resources and opportunities for everyone.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gray-50 border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8"
            >
              <h3 className="text-lg sm:text-xl font-black text-foreground mb-3 sm:mb-4">How often do you host events?</h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                We host regular events including workshops, hackathons, guest lectures, and social gatherings. Check our Events page or follow us on social media for the latest schedule.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gray-50 border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8"
            >
              <h3 className="text-lg sm:text-xl font-black text-foreground mb-3 sm:mb-4">Can I collaborate on research projects?</h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                Absolutely! We actively collaborate on research projects with faculty members and industry partners. Contact us if you're interested in getting involved in cutting-edge ML research.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer2 />
    </div>
  );
};

export default Contact;